import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AiAgent } from '@lgg/isomorphic/types/__generated__/graphql';
import { Table, TableColumns, TableProps } from 'src/components/general/display/table';
import { getDefaultSortOrder, TableSortData } from 'src/components/general/table-helpers';
import { getCreatedByColumn } from 'src/components/pages/ai-agents/components/ai-agents-table-columns';

const AgentName = styled(Link)`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px; /* 123.077% */
  cursor: pointer;
`;

const AgentType = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-weight: 400;
  font-size: 13px;
  line-height: 16px; /* 123.077% */
`;

type AiAgentsTableProps = {
  data: AiAgent[];
  sortData: TableSortData;
  onChange: TableProps<AiAgent>['onChange'];
  sectionUrl: string;
};

export const AiAgentsTable = memo<AiAgentsTableProps>(
  ({ data, sortData, onChange, sectionUrl }) => {
    const { t } = useTranslation(['aiAgents']);
    const { key: sortKey, direction: sortDirection } = sortData;

    const columns: TableColumns<AiAgent>[] = [
      {
        title: t('aiAgents:pages.agents.columnHeaders.name'),
        key: 'name',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(sortKey === 'name', sortDirection),
        render: (_, { id, name }) => (
          <AgentName
            to={`${sectionUrl}/agents/${id}/knowledge`}
            data-lgg-id="ai-agents-table-cell-name"
          >
            {name}
          </AgentName>
        ),
      },
      {
        title: t('aiAgents:pages.agents.columnHeaders.type'),
        key: 'typeName',
        width: '135px',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(sortKey === 'typeName', sortDirection),
        render: (_, { typeName }) => (
          <AgentType data-lgg-id="ai-agents-table-cell-type">
            {typeName ? t(`aiAgents:types.${typeName}`) : ''}
          </AgentType>
        ),
      },
      getCreatedByColumn<AiAgent>({
        title: t('aiAgents:pages.agents.columnHeaders.createdBy'),
        sortData,
        testId: 'ai-agents-table-cell-created-by',
      }),
    ];

    return (
      <Table
        rowKey={({ id }) => id}
        data-lgg-id="ai-agents-table"
        columns={columns}
        tableLayout="fixed"
        loading={false}
        dataSource={data}
        pagination={false}
        showHeader={true}
        scroll={{ x: 1069 }}
        rowClassName="ai-agents-table-row"
        onChange={onChange}
      />
    );
  },
);
